import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import {
  extractApiError,
  lookupPreregistration,
  rememberReservation,
  requestRecoveryLink,
} from '../api.js';

function formatEventDate(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * Page de récupération d'une préréservation par email.
 *
 * Flux hybride :
 *  1. L'utilisateur saisit son email → on affiche un récap **léger** (prénom/nom, repas, état,
 *     date de préréservation) — pas de montants, pas d'UUID, pas de commentaire.
 *  2. Bouton « Recevoir mon lien complet par email » → on envoie le lien magique vers
 *     `/payer/:uuid` qui donne accès aux montants et au paiement.
 */
export default function Recover() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [linkSending, setLinkSending] = useState(false);
  const [linkSent, setLinkSent] = useState(false);

  async function handleLookup(e) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setSubmitting(true);
    setError(null);
    setResult(null);
    setLinkSent(false);
    try {
      const data = await lookupPreregistration(trimmed);
      setResult(data);
      // On mémorise localement l'email (sans UUID, non exposé par cette route)
      // pour reconnaître le client lors de ses prochaines visites de la home.
      // Si une mémorisation antérieure avec UUID existe pour ce même email,
      // l'UUID est préservé grâce à la logique de `rememberReservation`.
      rememberReservation({
        email: trimmed,
        first_name: data.first_name,
        last_name: data.last_name,
        meals: data.meals,
      });
    } catch (err) {
      const code = err?.response?.status;
      if (code === 404) {
        setError(
          `Aucune préréservation active trouvée pour « ${trimmed} ». ` +
          `Vérifiez l'adresse, ou préréservez-vous depuis la page d'accueil.`,
        );
      } else {
        setError(extractApiError(err, 'Recherche impossible.'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSendLink() {
    setLinkSending(true);
    setError(null);
    try {
      await requestRecoveryLink(email.trim());
      setLinkSent(true);
    } catch (err) {
      setError(extractApiError(err, "Envoi du lien impossible."));
    } finally {
      setLinkSending(false);
    }
  }

  function resetSearch() {
    setResult(null);
    setLinkSent(false);
    setError(null);
  }

  const isReservation = result?.reservation_state === 'reservation';
  const isCancelled   = result?.reservation_state === 'cancelled';

  return (
    <Container maxWidth="sm">
      <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, border: 1, borderColor: 'divider' }}>
        <Stack spacing={3}>
          <Box>
            <Tooltip title="Retour à l'accueil">
              <IconButton
                component={RouterLink}
                to="/"
                size="small"
                aria-label="Retour à l'accueil"
                sx={{ mb: 1, ml: -1 }}
              >
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="overline" color="primary.main" sx={{ letterSpacing: 1.2 }}>
              Retrouver ma préréservation
            </Typography>
            <Typography variant="h4" sx={{ mt: 0.5 }}>
              Mon récapitulatif
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Saisissez l'adresse email utilisée lors de la préréservation. Si une préréservation
              active existe, nous vous afficherons un récapitulatif et pourrons vous renvoyer
              le lien complet par email.
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleLookup}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                label="Adresse email"
                type="email"
                fullWidth
                required
                value={email}
                onChange={(e) => { setEmail(e.target.value); resetSearch(); }}
                disabled={submitting}
                autoFocus
              />
              <Button
                type="submit"
                variant="contained"
                startIcon={<SearchIcon />}
                disabled={submitting || !email.trim()}
                sx={{ minWidth: { sm: 160 } }}
              >
                {submitting ? 'Recherche…' : 'Rechercher'}
              </Button>
            </Stack>
          </Box>

          {error && <Alert severity="warning">{error}</Alert>}

          {result && (
            <Paper
              variant="outlined"
              sx={(theme) => ({
                p: 2.5,
                // Fond legerement teinte qui s'adapte au mode : presque blanc
                // en light, sombre tamise en dark. Garantit que le texte par
                // defaut MUI reste lisible quelle que soit la palette active.
                bgcolor: theme.palette.mode === 'light' ? '#f8fafc' : 'action.hover',
              })}
            >
              <Stack spacing={2}>
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    {isReservation ? 'Réservation confirmée' : isCancelled ? 'Préréservation annulée' : 'Préréservation'}
                  </Typography>
                  <Typography variant="h6" sx={{ mt: 0.5 }}>
                    {result.first_name} {result.last_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {result.event_name} · {formatEventDate(result.event_date)}
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {isReservation && (
                    <Chip
                      icon={<EventAvailableOutlinedIcon />}
                      label="Place confirmée"
                      color="primary"
                      size="small"
                    />
                  )}
                  {!isReservation && !isCancelled && (
                    <Chip
                      icon={<HourglassEmptyOutlinedIcon />}
                      label="En attente de confirmation"
                      variant="outlined"
                      size="small"
                    />
                  )}
                  <Chip label={`${result.adults} adulte${result.adults > 1 ? 's' : ''}`} size="small" variant="outlined" />
                  <Chip label={`${result.children} enfant${result.children > 1 ? 's' : ''}`} size="small" variant="outlined" />
                  <Chip label={`${result.meals} repas`} size="small" variant="outlined" color="primary" />
                </Stack>

                <Typography variant="caption" color="text.secondary">
                  Préréservation enregistrée le {formatDate(result.created_at)}
                  {result.confirmed_at && ` · confirmée le ${formatDate(result.confirmed_at)}`}
                </Typography>

                <Divider />

                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Pour accéder au détail complet (montant à régler, paiement à venir),
                    nous vous envoyons un lien personnel par email :
                  </Typography>
                  {linkSent ? (
                    <Alert severity="success" icon={<EmailOutlinedIcon />}>
                      Lien envoyé à <strong>{email}</strong>. Vérifiez votre boîte de réception
                      (et éventuellement les indésirables).
                    </Alert>
                  ) : (
                    <Button
                      variant="contained"
                      startIcon={<SendIcon />}
                      onClick={handleSendLink}
                      disabled={linkSending}
                      fullWidth
                    >
                      {linkSending ? 'Envoi…' : 'Recevoir mon lien complet par email'}
                    </Button>
                  )}
                </Box>
              </Stack>
            </Paper>
          )}
        </Stack>
      </Paper>
    </Container>
  );
}
